import React, {useEffect, useMemo, useState} from "react";
import {
	Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Container, Dialog, DialogActions,
	DialogContent, DialogTitle, Divider, IconButton, Skeleton, Snackbar, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import {QRCodeSVG} from "qrcode.react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {useNavigate} from "react-router-dom";
import {
	Add as AddIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	QrCode as QrCodeIcon,
	CheckCircle as CheckCircleIcon,
	Refresh as RefreshIcon,
	InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import {useTheme, useMediaQuery} from "@mui/material";
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme} from "../../theme";
import {config} from "../../config";

// --- Use your existing base from your current code ---
const BASE_UPI = `${config.API_MSWSSTOREMANAGER_BASE_URL}/api/upi-payment-methods`;

// Basic (adjust if you need stricter)
const vpaRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;

export default function ManagePayments() {
	// data
	const [upis, setUpis] = useState([]);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState(null);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const navigate = useNavigate();

	// put these near the top
	// Helpers — always read latest values
	const getVendorId = () =>
		(sessionStorage.getItem("vendorId") || localStorage.getItem("vendorId") || "").trim();

	const getSessionToken = () =>
		(sessionStorage.getItem("sessionToken") || localStorage.getItem("sessionToken") || "").trim();

	const withVendor = (url) =>
		`${url}${url.includes("?") ? "&" : "?"}vendorId=${encodeURIComponent(getVendorId())}`;


	// dialogs
	const [editOpen, setEditOpen] = useState(false);
	const [editTarget, setEditTarget] = useState(null); // null = add, object = edit
	const [form, setForm] = useState({vpa: "", nameOnAccount: ""});
	const [saving, setSaving] = useState(false);

	const [deleteTarget, setDeleteTarget] = useState(null); // id (number) or null
	const [qrOpen, setQrOpen] = useState(false);

	// ui feedback
	const [snack, setSnack] = useState({open: false, msg: "", severity: "success"});

	const activeUpi = useMemo(() => upis.find(u => u.active) || null, [upis]);
	const sortedUpis = useMemo(() => {
		const arr = [...upis];
		arr.sort((a, b) => {
			if (a.active && !b.active) return -1;
			if (!a.active && b.active) return 1;
			return (a.nameOnAccount || "").localeCompare(b.nameOnAccount || "");
		});
		return arr;
	}, [upis]);
	const showActivationInfo = !loading && upis.length > 0 && !activeUpi;

	const fetchUpis = async () => {
		setLoading(true);
		setErr(null);
		try {
			const vid = getVendorId();
			const tok = getSessionToken();
			if (!vid) throw new Error("Missing vendorId (not in storage).");
			if (!tok) throw new Error("Missing session token (not in storage).");

			const url = withVendor(BASE_UPI);
			// optional: console.debug('GET', url, { hasToken: !!tok, vid });

			const res = await fetch(url, {
				method: "GET",
				headers: {"X-Session-Token": tok, Accept: "*/*", "Content-Type": "application/json"},
				cache: "no-store",
			});

			const raw = await res.text(); // read body even on 4xx to show details
			if (!res.ok) {
				let detail = raw;
				try {
					detail = raw ? JSON.parse(raw)?.message || raw : raw;
				} catch {
				}
				throw new Error(`GET failed: ${res.status} ${res.statusText}${detail ? " - " + detail : ""}`);
			}

			const data = raw ? JSON.parse(raw) : [];
			setUpis(Array.isArray(data) ? data : []);
		} catch (e) {
			console.error(e);
			setErr(String(e.message || "Failed to load UPI methods."));
		} finally {
			setLoading(false);
		}
	};


	useEffect(() => {
		fetchUpis();
	}, []);

	// --- Actions ---
	const openAdd = () => {
		setEditTarget(null);
		setForm({vpa: "", nameOnAccount: ""});
		setEditOpen(true);
	};

	const openEdit = (item) => {
		setEditTarget(item);
		setForm({vpa: item.vpa || "", nameOnAccount: item.nameOnAccount || ""});
		setEditOpen(true);
	};

	const closeEdit = () => {
		if (!saving) setEditOpen(false);
	};

	const onSave = async () => {
		const vpa = (form.vpa || "").trim();
		const nameOnAccount = (form.nameOnAccount || "").trim();

		if (!vpaRegex.test(vpa)) {
			setSnack({open: true, msg: "Please enter a valid VPA (e.g., name@bank).", severity: "warning"});
			return;
		}
		if (!nameOnAccount) {
			setSnack({open: true, msg: "Name on Account is required.", severity: "warning"});
			return;
		}

		setSaving(true);
		try {
			if (editTarget) {
				// PUT /{id}?vendorId=...
				const res = await fetch(withVendor(`${BASE_UPI}/${editTarget.upiPaymentMethodId}`), {
					method: "PUT",
					headers: {
						"Content-Type": "application/json;charset=UTF-8",
						"X-Session-Token": getSessionToken(),
					},
					body: JSON.stringify({
						upiPaymentMethodId: editTarget.upiPaymentMethodId,
						vpa,
						nameOnAccount,
						active: editTarget.active,
					}),
				});
				if (!res.ok) throw new Error(`PUT failed: ${res.status}`);
			} else {
				// POST /?vendorId=...
				const res = await fetch(withVendor(BASE_UPI), {
					method: "POST",
					headers: {
						"Content-Type": "application/json;charset=UTF-8",
						"X-Session-Token": getSessionToken(),
					},
					body: JSON.stringify({
						upiPaymentMethodId: null,
						vpa,
						nameOnAccount,
						active: false,
					}),
				});
				if (!res.ok) throw new Error(`POST failed: ${res.status}`);
			}
			setSnack({open: true, msg: "Saved successfully.", severity: "success"});
			setEditOpen(false);
			await fetchUpis();
		} catch (e) {
			console.error(e);
			setSnack({open: true, msg: "Save failed.", severity: "error"});
		} finally {
			setSaving(false);
		}
	};

	const onDelete = async () => {
		if (!deleteTarget) return;
		try {
			const res = await fetch(withVendor(`${BASE_UPI}/${deleteTarget}`), {
				method: "DELETE",
				headers: {"X-Session-Token": getSessionToken(), Accept: "*/*"},
				cache: "no-store",
			});
			if (!(res.status === 204 || res.ok)) throw new Error(`DELETE failed: ${res.status}`);
			setSnack({open: true, msg: "Deleted.", severity: "success"});
			setDeleteTarget(null);
			await fetchUpis();
		} catch (e) {
			console.error(e);
			setSnack({open: true, msg: "Delete failed.", severity: "error"});
		}
	};

	const setActive = async (id) => {
		try {
			const res = await fetch(withVendor(`${BASE_UPI}/${id}/status?active=true`), {
				method: "PATCH",
				headers: {"X-Session-Token": getSessionToken()},
			});
			if (!(res.status === 204 || res.ok)) throw new Error(`PATCH failed: ${res.status}`);
			setSnack({open: true, msg: "Set as active.", severity: "success"});
			await fetchUpis();
		} catch (e) {
			console.error(e);
			setSnack({open: true, msg: "Failed to set active.", severity: "error"});
		}
	};

	const openQr = () => setQrOpen(true);
	const closeQr = () => setQrOpen(false);

	// Build UPI URI with amount = 1 (as requested)
	const upiUri = activeUpi
		? `upi://pay?pa=${encodeURIComponent(activeUpi.vpa)}&pn=${encodeURIComponent(
			activeUpi.nameOnAccount || ""
		)}&am=1&cu=INR`
		: "";

	// --- UI helpers ---
	const EmptyState = () => (
		<Card elevation={0} sx={{border: t => `1px dashed ${t.palette.divider}`, borderRadius: 3}}>
			<CardContent>
				<Stack spacing={2} alignItems="center" textAlign="center">
					<Box sx={{fontSize: 56, lineHeight: 1}}>🧾</Box>
					<Typography variant="h6">No UPI accounts added</Typography>
					<Typography variant="body2" color="text.secondary" sx={{maxWidth: 420}}>
						Add your UPI VPA to enable payments and generate a QR code for quick checkout.
					</Typography>
					<Button startIcon={<AddIcon/>} variant="contained" onClick={openAdd}>
						Add UPI account
					</Button>
				</Stack>
			</CardContent>
		</Card>
	);

	const Row = ({item}) => {
		const isActive = !!item.active;

		return (
			<Card
				key={item.upiPaymentMethodId}
				sx={{
					mb: 2,
					borderRadius: 3,
					...(isActive
						? {
							border: t => `1px solid ${t.palette.success.light}`,
							boxShadow: t => `0 0 0 3px ${t.palette.success.light}33`,
							background: t => t.palette.success.light + "0A",
						}
						: {}),
				}}
			>
				<CardHeader
					title={
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="subtitle1" sx={{fontWeight: 600}}>
								{item.nameOnAccount}
							</Typography>
							{isActive && (
								<Chip
									size="small"
									label="Active"
									color="success"
									icon={<CheckCircleIcon fontSize="small"/>}
								/>
							)}
						</Stack>
					}
					subheader={
						<Typography variant="body2" color="text.secondary" sx={{wordBreak: "break-word"}}>
							VPA: {item.vpa}
						</Typography>
					}
					action={
						<Stack direction="row" spacing={0.5}>
							{/* Edit always available */}
							<Tooltip title="Edit">
								<IconButton onClick={() => openEdit(item)} size="small">
									<EditIcon fontSize="small"/>
								</IconButton>
							</Tooltip>
							{/* Delete always available */}
							<Tooltip title="Delete">
								<IconButton
									onClick={() => setDeleteTarget(item.upiPaymentMethodId)}
									size="small"
								>
									<DeleteIcon fontSize="small"/>
								</IconButton>
							</Tooltip>
						</Stack>
					}
				/>
				<Divider/>
				<CardContent>
					<Stack
						direction={{xs: "column", sm: "row"}}
						spacing={1.5}
						alignItems={{xs: "stretch", sm: "center"}}
						justifyContent="flex-start"
					>
						{isActive ? (
							<Button
								variant="outlined" color="secondary"
								startIcon={<QrCodeIcon/>} sx={{fontWeight: 600}}
								onClick={openQr}
							>
								View QR code
							</Button>
						) : (
							<Button
								variant="contained" sx={{fontWeight: 600}}
								onClick={() => setActive(item.upiPaymentMethodId)}
							>
								Set Active
							</Button>
						)}
					</Stack>
				</CardContent>
			</Card>
		);
	};

	return (
		<Container maxWidth="sm" sx={{py: 4}}>
			<ThemeProvider theme={theme}>
				<Box
					sx={{
						mb: 4,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					{/* Left: Back + Title */}
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{minWidth: 0, flex: "1 1 auto"}}
					>
						{isMobile ? (
							<IconButton
								onClick={() => navigate("/")}
								aria-label="Go back"
								size="small"
								sx={{
									width: 36,
									height: 36,
									borderRadius: 999,
									border: (t) => `1px solid ${t.palette.divider}`,
									backgroundColor: (t) =>
										t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
									boxShadow: (t) => (t.palette.mode === "dark" ? "none" : "0 1px 0 rgba(0,0,0,0.04)"),
									"&:hover": {backgroundColor: (t) => t.palette.action.hover},
									"&:active": {transform: "translateY(1px)"},
									transition: "transform 120ms ease",
								}}
							>
								<ArrowBackIosNewIcon fontSize="small"/>
							</IconButton>
						) : (
							<Button
								onClick={() => navigate("/")}
								startIcon={<ArrowBackIosNewIcon/>}
								aria-label="Go back"
								variant="outlined" color="secondary"
								size="small"
								sx={{
									borderRadius: 999,
									textTransform: "none",
									px: 1.25,
									py: 0.75,
									fontWeight: 600,
									borderColor: "divider",
									backgroundColor: (t) =>
										t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
									boxShadow: (t) => (t.palette.mode === "dark" ? "none" : "0 1px 0 rgba(0,0,0,0.04)"),
									"&:hover": {backgroundColor: (t) => t.palette.action.hover},
									"& .MuiButton-startIcon": {mr: 0.5},
								}}
							>
								Back
							</Button>
						)}

						<Typography
							variant={isMobile ? "h6" : "h5"}
							noWrap
							sx={{fontWeight: 700, minWidth: 0, ml: {xs: 0.5, sm: 0}}}
							title="Manage UPI"
						>
							Manage UPI
						</Typography>
					</Stack>

					{/* Right: Actions */}
					<Stack direction="row" spacing={1} alignItems="center" sx={{flexShrink: 0}}>
						<Tooltip title="Refresh">
							<IconButton onClick={fetchUpis} size={isMobile ? "small" : "medium"}>
								<RefreshIcon/>
							</IconButton>
						</Tooltip>
						<Button
							startIcon={<AddIcon/>}
							variant="contained"
							onClick={openAdd}
							sx={{whiteSpace: "nowrap"}}
						>
							Add UPI
						</Button>
					</Stack>
				</Box>

				{showActivationInfo && (
					<Box
						sx={{
							mb: 3,
							borderRadius: 3,
							border: (t) => `1px solid ${t.palette.info.light}`,
							background: (t) =>
								t.palette.mode === "dark"
									? "linear-gradient(90deg, rgba(21,101,192,0.25), rgba(21,101,192,0.05))"
									: "linear-gradient(90deg, rgba(2,136,209,0.18), rgba(2,136,209,0.05))",
							p: {xs: 2, sm: 2.5},
						}}
					>
						<Stack direction="row" spacing={2} alignItems="center">
							<InfoOutlinedIcon color="info" sx={{fontSize: 34}}/>
							<Box>
								<Typography variant="subtitle1" sx={{fontWeight: 700}}>
									Activate a UPI to start receiving payouts
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Set one of your saved UPI IDs as active to enable settlements and QR collections.
								</Typography>
							</Box>
						</Stack>
					</Box>
				)}

				{err && (
					<Alert severity="error" sx={{mb: 2}} onClose={() => setErr(null)}>
						{err}
					</Alert>
				)}

				{loading ? (
					<Card sx={{borderRadius: 3, mb: 2}}>
						<CardContent>
							<Stack spacing={2}>
								<Skeleton variant="text" width="60%"/>
								<Skeleton variant="text" width="40%"/>
								<Skeleton variant="rounded" height={44}/>
							</Stack>
						</CardContent>
					</Card>
				) : upis.length === 0 ? (
					<EmptyState/>
				) : (
					sortedUpis.map((u) => <Row key={u.upiPaymentMethodId} item={u}/>)
				)}

				{/* Add/Edit dialog */}
				<Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
					<DialogTitle>{editTarget ? "Edit UPI account" : "Add UPI account"}</DialogTitle>
					<DialogContent>
						<Stack spacing={2} sx={{pt: 1}}>
							<TextField
								label="VPA"
								placeholder="name@bank"
								value={form.vpa}
								onChange={(e) => setForm((p) => ({...p, vpa: e.target.value}))}
								fullWidth
								inputProps={{inputMode: "email"}}
								helperText="Format: name@bank (no spaces)"
							/>
							<TextField
								label="Name on Account"
								placeholder="e.g., John Doe"
								value={form.nameOnAccount}
								onChange={(e) => setForm((p) => ({...p, nameOnAccount: e.target.value}))}
								fullWidth
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeEdit} disabled={saving} color="secondary">Cancel</Button>
						<Button variant="contained" onClick={onSave} disabled={saving}>
							{saving ? <CircularProgress size={20}/> : "Save"}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Delete confirm */}
				<Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
					<DialogTitle>Delete UPI account</DialogTitle>
					<DialogContent>
						<Typography variant="body2" color="text.secondary">
							Are you sure you want to delete this UPI account?
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteTarget(null)} color="secondary">Cancel</Button>
						<Button color="error" variant="contained" onClick={onDelete}>
							Delete
						</Button>
					</DialogActions>
				</Dialog>

				{/* QR dialog (mount your own QR generator into the slot below) */}
				<Dialog open={qrOpen} onClose={closeQr} fullWidth maxWidth="xs">
					<DialogTitle>UPI QR Code</DialogTitle>
					<DialogContent>
						<Box sx={{display: "flex", justifyContent: "center", py: 2}}>
							{upiUri ? (
								<QRCodeSVG value={upiUri} size={220} level="M" includeMargin/>
							) : (
								<Typography variant="body2" color="text.secondary">
									Set an active UPI to generate a QR.
								</Typography>
							)}

							<div id="upi-qr-slot" data-qr-value={upiUri}/>
						</Box>
						<Alert severity="info">
							This QR encodes your active UPI with amount <strong>₹1</strong>.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button color="error" sx={{fontWeight: 600}} onClick={closeQr}>Close</Button>
					</DialogActions>
				</Dialog>

				<Snackbar
					open={snack.open}
					autoHideDuration={3000}
					onClose={() => setSnack((p) => ({...p, open: false}))}
					anchorOrigin={{vertical: "bottom", horizontal: "center"}}
				>
					<Alert
						onClose={() => setSnack((p) => ({...p, open: false}))}
						severity={snack.severity}
						variant="filled"
						sx={{width: "100%"}}
					>
						{snack.msg}
					</Alert>
				</Snackbar>
			</ThemeProvider>
		</Container>
	);
}
